import { useCallback, useMemo } from "react";
import HeroStatusChip from "@/components/HeroStatusChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  AlertTriangle,
  BarChart3,
  Check,
  Clock,
  LineChart,
  TrendingUp,
  Users,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import {
  buildAccuracyGaugeStyle,
  buildActionQueue,
  buildBlockWorkloadSummary,
  buildCategoryBars,
  buildDropboxExecutiveMarkdown,
  buildExecutiveInsights,
  buildHeroNarrative,
  buildHeroTitle,
  buildManagementProfile,
  buildPrimaryExecutiveAction,
  buildQualityDonutStyle,
  buildTopBlocks,
  fmt,
  formatCompactK,
  formatLakhShort,
  metricProgressWidths,
  pct,
} from "@/utils/dropboxDerived";
import "./DropboxStateOverviewTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const INSIGHT_ICONS = {
  green: Check,
  orange: Clock,
  red: AlertTriangle,
  purple: BarChart3,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: Clock,
  purple: Users,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DropboxStateOverviewTab = ({
  overview = {},
  categoryData = [],
  blockData = [],
  dataQuality = {},
  managementWiseData = [],
  onViewBlocks,
  onViewActions,
}) => {
  const accuracy = overview.data_accuracy ?? 0;
  const validCount = dataQuality.valid_count ?? 0;
  const pendingCount = dataQuality.pending_count ?? overview.active_import ?? 0;
  const errorCount = dataQuality.error_count ?? overview.wrong_entry ?? 0;
  const hasData = Boolean(overview?.total_remarks || overview?.schools_with_remarks);

  const buildLocalMarkdown = useCallback(
    () => buildDropboxExecutiveMarkdown(overview, dataQuality, blockData),
    [overview, dataQuality, blockData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ r: overview?.total_remarks, a: accuracy }),
    [overview?.total_remarks, accuracy]
  );

  const { loading: aiLoading, pickFromBucket, pickPrimaryAction } = useMistralNarrativeOverlay({
    kind: "dropbox-executive",
    title: "Dropbox Executive Summary",
    buildLocalMarkdown,
    context: { overview, dataQuality, blockData },
    enabled: hasData,
    scopeKey,
  });

  const localHeroNarrative = useMemo(
    () => buildHeroNarrative(overview, dataQuality),
    [overview, dataQuality]
  );
  const heroNarrative = useMemo(
    () => (aiLoading ? localHeroNarrative : pickFromBucket(localHeroNarrative, "insights", 0)),
    [aiLoading, pickFromBucket, localHeroNarrative]
  );
  const localPrimaryAction = useMemo(
    () => buildPrimaryExecutiveAction(overview, blockData),
    [overview, blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );

  const categoryBars = useMemo(() => buildCategoryBars(categoryData), [categoryData]);
  const insights = useMemo(
    () => buildExecutiveInsights(overview, dataQuality),
    [overview, dataQuality]
  );
  const topBlocks = useMemo(() => buildTopBlocks(blockData), [blockData]);
  const blockSummary = useMemo(() => buildBlockWorkloadSummary(blockData), [blockData]);
  const management = useMemo(
    () => buildManagementProfile(overview, managementWiseData),
    [overview, managementWiseData]
  );
  const actions = useMemo(() => buildActionQueue(overview), [overview]);
  const progress = useMemo(() => metricProgressWidths(overview), [overview]);

  const donutStyle = buildQualityDonutStyle(
    dataQuality.valid_pct ?? 0,
    dataQuality.error_pct ?? 0
  );
  const gaugeStyle = buildAccuracyGaugeStyle(accuracy);

  const qualityLegend = [
    {
      name: "Valid Data",
      count: validCount,
      share: dataQuality.valid_pct ?? 0,
      dotClass: "",
    },
    {
      name: "Wrong / Duplicate",
      count: errorCount,
      share: dataQuality.error_pct ?? 0,
      dotClass: "red",
    },
    {
      name: "Pending Import",
      count: pendingCount,
      share: dataQuality.pending_pct ?? 0,
      dotClass: "orange",
    },
  ];

  const metricCards = [
    {
      label: "Total Schools",
      value: fmt(overview.total_schools),
      sub: "Selected district scope",
      className: "",
      progress: progress.schools,
      barColor: "var(--dbx-primary)",
    },
    {
      label: "Schools Reporting",
      value: fmt(overview.schools_with_remarks),
      sub: `${pct(overview.reporting_rate)} penetration`,
      className: "",
      progress: progress.reporting,
      barColor: "var(--dbx-primary)",
    },
    {
      label: "Total Remarks",
      value: fmt(overview.total_remarks),
      sub: `${overview.avg_remarks_per_school} avg / school`,
      className: "",
      progress: progress.remarks,
      barColor: "var(--dbx-primary)",
    },
    {
      label: "Dropout Count",
      value: fmt(overview.dropout),
      sub: `${pct(overview.dropout_pct, 2)} of remarks`,
      className: "danger",
      progress: progress.dropout,
      barColor: "var(--dbx-danger)",
    },
    {
      label: "Pending Import",
      value: fmt(overview.active_import),
      sub: "Needs block-level closure",
      className: "warning",
      progress: progress.pending,
      barColor: "var(--dbx-warning)",
    },
    {
      label: "Wrong / Duplicate",
      value: fmt(overview.wrong_entry),
      sub: "Highest cleanup queue",
      className: "danger",
      progress: progress.wrong,
      barColor: "var(--dbx-danger)",
    },
  ];

  return (
    <div className="dbx-overview-tab" data-testid="dropbox-state-overview">
      <section className="dbx-hero-card" id="dbx-overview-hero">
        <div className="dbx-hero-layout">
          <div>
            <div className="dbx-health-main">
              <div
                className="dbx-gauge"
                style={gaugeStyle}
                aria-label={`Data accuracy ${accuracy} percent`}
              >
                <div>
                  <strong>{pct(accuracy)}</strong>
                  <span>Accuracy</span>
                </div>
              </div>
              <div className="dbx-health-copy">
                <h2>{toTitleCase(buildHeroTitle(accuracy))}</h2>
                <p>{heroNarrative}</p>
                <div className="dbx-status-row">
                  <HeroStatusChip chipClass="dbx-status-chip" variant="green">
                    {fmt(validCount)} valid records
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="dbx-status-chip"
                    variant="orange"
                    onClick={() => scrollToId("dbx-quality")}
                    ariaLabel={`${pendingCount} pending import records — view data quality section`}
                  >
                    {fmt(pendingCount)} pending import
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="dbx-status-chip"
                    variant="red"
                    onClick={() => scrollToId("dbx-actions")}
                    ariaLabel={`${errorCount} duplicate or wrong records — view action queue`}
                  >
                    {fmt(errorCount)} duplicate/wrong
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="dbx-summary-note">
              <div className="dbx-icon-box orange">
                <LineChart size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="dbx-metric-grid" aria-label="Dropbox KPIs">
            {metricCards.map((card) => (
              <article key={card.label} className={`dbx-metric-card ${card.className}`}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span className="dbx-metric-sub">{card.sub}</span>
                <div className="dbx-mini-progress">
                  <i style={{ width: `${card.progress}%`, background: card.barColor }} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dbx-insight-strip" aria-label="Executive insights">
        {insights.map((item) => {
          const Icon = INSIGHT_ICONS[item.iconClass] ?? BarChart3;
          return (
            <article key={item.id} className="dbx-insight-card">
              <div className={`dbx-icon-box ${item.iconClass}`}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(item.title)}</h3>
              <p>{item.body}</p>
            </article>
          );
        })}
      </section>

      <div className="dbx-section-title" id="dbx-quality">
        <div>
          <h2>Remarks Quality and Category Distribution</h2>
          <p>
            The existing long category chart is split into a readable category ranking and a quality
            donut, so officers can immediately see what needs action.
          </p>
        </div>
        <button
          type="button"
          className="dbx-section-action"
          onClick={() => (onViewBlocks ? onViewBlocks() : scrollToId("dbx-blocks"))}
        >
          View block load →
        </button>
      </div>

      <section className="dbx-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Remark Category Distribution</h3>
              <p>
                Sorted by operational volume, with micro categories retained but visually
                de-emphasized.
              </p>
            </div>
            <span className="dbx-pill">Volume</span>
          </div>
          <div className="dbx-bar-list" aria-label="Remark category distribution bar chart">
            {categoryBars.map((row) => (
              <div key={row.label} className="dbx-hbar-row">
                <div className="dbx-hbar-label">
                  {row.label}
                  <span className="dbx-hbar-sub">{row.sub}</span>
                </div>
                <div className="dbx-hbar-track">
                  <div
                    className={`dbx-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="dbx-hbar-value">{row.displayValue}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Data Quality Distribution</h3>
              <p>Quality is shown as a decision summary, not just a decorative chart.</p>
            </div>
            <span className="dbx-pill green">{pct(accuracy)} accuracy</span>
          </div>
          <div className="dbx-quality-layout">
            <div
              className="dbx-quality-donut"
              style={donutStyle}
              aria-label="Quality distribution donut"
            >
              <div className="dbx-donut-copy">
                <strong>{formatLakhShort(overview.total_remarks)}</strong>
                <span>Remarks</span>
              </div>
            </div>
            <div className="dbx-legend-list">
              {qualityLegend.map((row) => (
                <div key={row.name} className="dbx-legend-row">
                  <div className="dbx-legend-name">
                    <i className={`dbx-legend-dot ${row.dotClass}`.trim()} />
                    {row.name}
                  </div>
                  <div className="dbx-legend-value">
                    <b>{fmt(row.count)}</b>
                    <span>{pct(row.share)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-management">
        <div>
          <h2>Management View Simplified</h2>
          <p>
            The current 100% donut does not add insight. This version turns it into a compact
            management profile with a table for transparency.
          </p>
        </div>
        <button
          type="button"
          className="dbx-section-action"
          onClick={() => (onViewActions ? onViewActions() : scrollToId("dbx-actions"))}
        >
          View actions →
        </button>
      </div>

      <section className="dbx-management-grid">
        <article className="dbx-profile-card">
          <div className="dbx-profile-label">Management segmentation</div>
          <h3>{management.label} · {management.sharepct}%</h3>
          <p>
            {management.needsClassification
              ? 'All current records are grouped under "Other". Instead of showing an empty-looking donut, the dashboard highlights that management classification needs enrichment.'
              : `Records are segmented by ${management.label} management type for officer review.`}
          </p>
          <div className="dbx-profile-metrics">
            <div className="dbx-profile-mini">
              <strong>{fmt(overview.total_schools)}</strong>
              <span>Schools tagged</span>
            </div>
            <div className="dbx-profile-mini">
              <strong>{fmt(overview.total_remarks)}</strong>
              <span>Total remarks</span>
            </div>
            <div className="dbx-profile-mini">
              <strong>{overview.avg_remarks_per_school}</strong>
              <span>Avg / school</span>
            </div>
            <div className="dbx-profile-mini">
              <strong>{pct(overview.dropout_pct, 1)}</strong>
              <span>Dropout share</span>
            </div>
          </div>
        </article>

        <article className="dbx-panel dbx-table-card">
          <div className="dbx-table-head">
            <div>
              <h3>Management-Wise Summary</h3>
              <p>Single-row view retained for auditability and easy validation.</p>
            </div>
            {management.needsClassification && (
              <span className="dbx-pill orange">Classification pending</span>
            )}
          </div>
          <table className="dbx-overview-table" aria-label="Management summary table">
            <thead>
              <tr>
                <th>Management</th>
                <th>Schools</th>
                <th>Total Remarks</th>
                <th>Avg / School</th>
                <th>Dropout</th>
                <th>Dropout %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {management.rows.map((row) => (
                <tr key={row.management}>
                  <td>{row.management}</td>
                  <td>{fmt(row.school_count)}</td>
                  <td>{fmt(row.total_remarks)}</td>
                  <td className="dbx-score">{row.avg_remarks_per_school}</td>
                  <td>{fmt(row.dropout)}</td>
                  <td>
                    <span className="dbx-tag red">{pct(row.dropout_pct, 1)}</span>
                  </td>
                  <td>
                    {management.needsClassification ? (
                      <span className="dbx-tag orange">Needs classification</span>
                    ) : (
                      <span className="dbx-tag green">Classified</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-blocks">
        <div>
          <h2>Top Operational-Load Blocks</h2>
          <p>
            Block ranking is redesigned as a scan-friendly workload list. This helps officers
            quickly decide where follow-up capacity should go first.
          </p>
        </div>
        <button
          type="button"
          className="dbx-section-action"
          onClick={() => scrollToId("dbx-overview-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="dbx-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Top 10 Blocks by Remarks Volume</h3>
              <p>Ranking based on total operational load.</p>
            </div>
            <span className="dbx-pill">Top 10</span>
          </div>
          <div className="dbx-block-ranking" aria-label="Top ten blocks by operational load">
            {topBlocks.map((block) => (
              <div key={block.block_name} className="dbx-block-row">
                <div className="dbx-rank">{block.rank}</div>
                <div className="dbx-block-name">
                  <b>
                    <BlockLink blockCode={block.block_code}>{block.block_name}</BlockLink>
                  </b>
                  <span>{block.loadLabel}</span>
                </div>
                <div className="dbx-block-bar">
                  <i style={{ width: `${block.barPct}%` }} />
                </div>
                <div className="dbx-block-value">{block.displayRemarks}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel dbx-table-card">
          <div className="dbx-table-head">
            <div>
              <h3>Block Workload Summary</h3>
              <p>Compact table for block-level officer review.</p>
            </div>
            <span className="dbx-pill">Operational load</span>
          </div>
          <table className="dbx-overview-table" aria-label="Block workload summary">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Remarks</th>
                <th>Priority</th>
                <th>Suggested Focus</th>
              </tr>
            </thead>
            <tbody>
              {blockSummary.map((row) => (
                <tr key={row.block_name}>
                  <td>{row.rank}</td>
                  <td>
                    <BlockLink blockCode={row.block_code}>{row.block_name}</BlockLink>
                  </td>
                  <td className="dbx-score">{formatCompactK(row.total_remarks)}</td>
                  <td>
                    <span className={`dbx-tag ${row.priorityClass}`}>{row.priority}</span>
                  </td>
                  <td>{row.focus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-actions">
        <div>
          <h2>Recommended Action Queue</h2>
          <p>Action cards convert the state overview into a simple governance agenda for weekly reviews.</p>
        </div>
      </div>

      <section className="dbx-action-grid">
        {actions.map((item) => {
          const Icon = ACTION_ICONS[item.iconClass] ?? TrendingUp;
          return (
            <div key={item.title} className="dbx-action-item">
              <div className={`dbx-icon-box ${item.iconClass}`}>
                <Icon size={22} />
              </div>
              <div>
                <h4>{item.title}</h4>
                <p>{item.body}</p>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default DropboxStateOverviewTab;
